import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { getApiUrl } from '../config/api.config';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemIcon,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Alert,
  CircularProgress,
  Tooltip,
  Fab,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  QrCodeScanner as ScannerIcon,
  Receipt as ReceiptIcon,
  ShoppingCart as CartIcon,
  Search as SearchIcon,
  ClearAll as ClearAllIcon,
  Print as PrintIcon,
  Save as SaveIcon,
  List as ListIcon,
  Edit as EditIcon,
  PlayArrow as PlayArrowIcon,
  AttachMoney as PriceIcon,
  Close as CloseIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { formatCurrency } from '../utils/currency';
import { PaymentMethod, Product, User, Role, Discount, DiscountType } from '../types';
import { useGetProductsQuery, useQuickSearchProductsQuery, useSearchProductsWithPatternQuery, useGetProductByBarcodeQuery } from '../services/products.service';
import { useGetUserByEmployeeCodeQuery } from '../services/users.service';
import { useCreateSaleMutation, useSaveDraftMutation, useGetDraftsQuery, useDeleteDraftMutation, useCompleteDraftMutation } from '../services/sales.service';
import { useDiscounts } from '../hooks/useDiscounts';

interface CartItem {
  productId: string;
  name: string;
  sku: string;
  barcode?: string;
  price: number;
  quantity: number;
  taxRate: number;
  stock: number;
  priceLevel?: number;
  priceLevelLabel?: string;
}

interface PriceOption {
  level: number;
  label: string;
  price: number;
}

interface ProductWithStock extends Product {
  stock: number; // We'll calculate this from inventories
}

const SalesPage: React.FC = () => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(PaymentMethod.CASH);
  const [openCheckout, setOpenCheckout] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<ProductWithStock | null>(null);
  const [quickQuantity, setQuickQuantity] = useState(1);
  const [isProcessingBarcode, setIsProcessingBarcode] = useState(false);
  const [lastScannedBarcode, setLastScannedBarcode] = useState('');
  const [employeeCode, setEmployeeCode] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState<User | null>(null);
  const [barcodeFieldFocused, setBarcodeFieldFocused] = useState(false);
  const [barcodeError, setBarcodeError] = useState('');
  const [lastAddedProductId, setLastAddedProductId] = useState<string | null>(null);
  // Predefined discounts disabled for security - keeping variable for backward compatibility
  const [selectedDiscountId, setSelectedDiscountId] = useState<string>('');
  const [manualDiscountAmount, setManualDiscountAmount] = useState<number>(0);
  const [openCustomProductDialog, setOpenCustomProductDialog] = useState(false);
  const [customProductName, setCustomProductName] = useState('');
  const [customProductPrice, setCustomProductPrice] = useState<number>(0);
  const [discountApplied, setDiscountApplied] = useState<{
    type: DiscountType;
    name: string;
    percentage?: number;
    fixedAmount?: number;
    isManual?: boolean;
  } | null>(null);
  
  // Draft related state
  const [openSaveDraftDialog, setOpenSaveDraftDialog] = useState(false);
  const [openDraftsDialog, setOpenDraftsDialog] = useState(false);
  const [draftName, setDraftName] = useState('');

  // Price selection state
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [selectedCartItem, setSelectedCartItem] = useState<CartItem | null>(null);
  const [selectedItem, setSelectedItem] = useState<CartItem | null>(null);
  const [priceOptions, setPriceOptions] = useState<PriceOption[]>([]);
  const [editingQuantity, setEditingQuantity] = useState<{[key: string]: string}>({});
  
  const searchRef = useRef<HTMLInputElement>(null);
  const barcodeRef = useRef<HTMLInputElement>(null);
  
  // Hooks para API
  const { data: employeeData } = useGetUserByEmployeeCodeQuery(employeeCode, {
    skip: !employeeCode
  });
  const [createSale, { isLoading: isCreatingSale }] = useCreateSaleMutation();
  const [saveDraft, { isLoading: isSavingDraft }] = useSaveDraftMutation();
  const { data: draftsData, refetch: refetchDrafts } = useGetDraftsQuery({ page: 1, limit: 50 });
  const [deleteDraft, { isLoading: isDeletingDraft }] = useDeleteDraftMutation();
  const [completeDraft, { isLoading: isCompletingDraft }] = useCompleteDraftMutation();
  const { getActiveDiscounts, calculateDiscountAmount } = useDiscounts();
  
  // Get current user from auth state
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const isAdmin = currentUser?.role === Role.ADMIN;
  const isManager = currentUser?.role === Role.MANAGER;
  const isCashier = currentUser?.role === Role.CASHIER;
  const canApplyManualDiscount = isAdmin || isManager || isCashier;
  
  // Predefined discounts disabled for security
  // const availableDiscounts = getActiveDiscounts();

  // Focus management is handled by aggressive focus manager - no helper function needed

  // Debounce search term to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Actualizar empleado seleccionado cuando se encuentre uno
  useEffect(() => {
    if (employeeData?.success && employeeData.data) {
      setSelectedEmployee(employeeData.data);
    } else {
      setSelectedEmployee(null);
    }
  }, [employeeData]);

  // Focus management is now handled by the aggressive focus manager below

  // Detectar si es un patrón wildcard
  const isWildcardPattern = debouncedSearchTerm.startsWith('*') && debouncedSearchTerm.length >= 5;
  
  // Fetch products from API with optimized search
  const {
    data: productsResponse,
    isLoading: isLoadingProducts,
    error: productsError
  } = useQuickSearchProductsQuery({
    q: debouncedSearchTerm,
    limit: 50
  }, {
    skip: !debouncedSearchTerm || debouncedSearchTerm.length < 1 || isWildcardPattern
  });

  // Fetch products with wildcard pattern
  const {
    data: wildcardResponse,
    isLoading: isLoadingWildcard,
    error: wildcardError
  } = useSearchProductsWithPatternQuery({
    pattern: debouncedSearchTerm,
    limit: 50
  }, {
    skip: !isWildcardPattern
  });

  // Combinar productos de búsqueda normal y wildcard
  const allProducts = isWildcardPattern ? (wildcardResponse?.data || []) : (productsResponse?.products || []);
  
  // Transform API products into ProductWithStock format
  const products: ProductWithStock[] = allProducts.map(product => ({
    ...product,
    stock: product.inventories?.reduce((total, inv) => total + inv.quantity, 0) || 0
  }));

  // Products are already filtered by the API based on search term
  const filteredProducts = products;

  // Aggressive focus function - single purpose, direct approach
  const focusBarcodeForContinuousScanning = (forceFocus: boolean = false) => {
    console.log('🎯 Starting focus attempt, force:', forceFocus);
    
    // Check if dialogs are open
    const dialogsOpen = openCheckout || openSaveDraftDialog || openDraftsDialog || openCustomProductDialog || priceDialogOpen;
    
    if (dialogsOpen) {
      console.log('🎯 Skipping - dialogs open');
      return;
    }
    
    if (!barcodeRef.current) {
      console.log('🎯 Skipping - no ref');
      return;
    }
    
    if (isProcessingBarcode && !forceFocus) {
      console.log('🎯 Skipping - processing without force');
      return;
    }
    
    // Aggressive direct focus
    const htmlInput = barcodeRef.current.querySelector('input') as HTMLInputElement;
    if (htmlInput) {
      htmlInput.focus();
      console.log('🎯 Focused HTML input directly');
      
      // Verify it worked
      setTimeout(() => {
        if (document.activeElement === htmlInput) {
          console.log('✅ Focus SUCCESS - HTML input is active');
        } else {
          console.log('❌ Focus FAILED - trying backup');
          // Backup attempt
          const inputById = document.getElementById('barcode-input-field') as HTMLInputElement;
          if (inputById) {
            const nestedInput = inputById.querySelector('input') as HTMLInputElement;
            if (nestedInput) {
              nestedInput.focus();
              console.log('🎯 Backup focus attempted');
            }
          }
        }
      }, 50);
    }
  };

  // Funciones del carrito
  const addToCart = (product: ProductWithStock, quantity: number = 1, fromBarcodeScan: boolean = false) => {
    console.log('🛒 Adding to cart:', product.name, 'from barcode scan:', fromBarcodeScan);

    const existingItem = cart.find(item => item.productId === product.id);

    if (existingItem) {
      // If item exists, move it to the top with updated quantity
      setCart(prev => {
        const updatedItem = { ...existingItem, quantity: existingItem.quantity + quantity };
        const otherItems = prev.filter(item => item.productId !== product.id);
        return [updatedItem, ...otherItems];
      });
    } else {
      // Add new item at the beginning of the cart
      setCart(prev => [{
        productId: product.id,
        name: product.name,
        sku: product.sku,
        barcode: product.barcode,
        price: product.salePrice,
        quantity,
        taxRate: typeof product.taxRate === 'number' && !isNaN(product.taxRate) ? product.taxRate : 15,
        stock: product.stock,
      }, ...prev]);
    }
    
    // Track the last added product to highlight it
    setLastAddedProductId(product.id);
    
    // Clear the highlight after 3 seconds
    setTimeout(() => {
      setLastAddedProductId(null);
    }, 3000);
    
    // Clear search term if adding from search
    if (!fromBarcodeScan) {
      setSearchTerm('');
      setSelectedProduct(null);
    }
    
    // Always return focus to barcode field for continuous scanning
    // Force focus if this was from a barcode scan (to override isProcessingBarcode check)
    focusBarcodeForContinuousScanning(fromBarcodeScan);
  };

  const updateQuantity = (productId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      setCart(cart.filter(item => item.productId !== productId));
    } else {
      setCart(cart.map(item =>
        item.productId === productId
          ? { ...item, quantity: newQuantity }
          : item
      ));
    }
  };

  const removeFromCart = (productId: string) => {
    setCart(cart.filter(item => item.productId !== productId));
    // Clean up editing state for removed item
    setEditingQuantity(prev => {
      const newState = { ...prev };
      delete newState[productId];
      return newState;
    });
    
    // Return focus after removing item
    focusBarcodeForContinuousScanning();
  };

  const clearCart = () => {
    setCart([]);
    setCustomerName('');
    setPaymentMethod(PaymentMethod.CASH);
    setSearchTerm('');
    setSelectedProduct(null);
    setSelectedDiscountId('');
    setManualDiscountAmount(0);
    setDiscountApplied(null);
    setEditingQuantity({}); // Clear all editing states
    
    // Return focus for continuous scanning after clearing cart
    focusBarcodeForContinuousScanning();
  };

  // Get price options for a product
  const getPriceOptions = (product: Product): PriceOption[] => {
    const options: PriceOption[] = [
      { level: 0, label: 'Precio Regular', price: Number(product.salePrice) }
    ];
    
    if (product.discountPrice1 && Number(product.discountPrice1) > 0) {
      options.push({ level: 1, label: 'Precio Descuento 1', price: Number(product.discountPrice1) });
    }
    if (product.discountPrice2 && Number(product.discountPrice2) > 0) {
      options.push({ level: 2, label: 'Precio Descuento 2', price: Number(product.discountPrice2) });
    }
    if (product.discountPrice3 && Number(product.discountPrice3) > 0) {
      options.push({ level: 3, label: 'Precio Descuento 3', price: Number(product.discountPrice3) });
    }
    if (product.discountPrice4 && Number(product.discountPrice4) > 0) {
      options.push({ level: 4, label: 'Precio Descuento 4', price: Number(product.discountPrice4) });
    }

    return options;
  };

  // Handle price selection
  const handlePriceSelection = async (cartItem: CartItem) => {
    console.log('💰 handlePriceSelection called for:', cartItem);
    console.log('💰 productsResponse available:', !!productsResponse?.products);
    console.log('💰 wildcardResponse available:', !!wildcardResponse?.data);

    // First try to find the product in current search results
    let product = productsResponse?.products.find((p: Product) => p.id === cartItem.productId);
    console.log('💰 Found in search results:', !!product);

    // If not found, try wildcard search results
    if (!product) {
      product = wildcardResponse?.data.find((p: Product) => p.id === cartItem.productId);
      console.log('💰 Found in wildcard results:', !!product);
    }

    // If still not found, try to fetch it directly using the products API
    if (!product && cartItem.barcode) {
      console.log('💰 Trying to fetch by barcode:', cartItem.barcode);
      try {
        const response = await fetch(`${getApiUrl()}/products/barcode/${cartItem.barcode}`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          product = data.data;
          console.log('💰 Found via barcode API:', !!product);
        } else {
          console.log('💰 Barcode API failed:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('💰 Error fetching product by barcode:', error);
      }
    }

    // If still not found, try to fetch by product ID
    if (!product) {
      console.log('💰 Trying to fetch by product ID:', cartItem.productId);
      try {
        const response = await fetch(`${getApiUrl()}/products/${cartItem.productId}`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          product = data.data;
          console.log('💰 Found via ID API:', !!product);
        } else {
          console.log('💰 ID API failed:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('💰 Error fetching product by ID:', error);
      }
    }

    if (product) {
      console.log('💰 Product found, getting price options:', product.name);
      const options = getPriceOptions(product);
      console.log('💰 Price options:', options);
      setPriceOptions(options);
      setSelectedItem(cartItem);
      setPriceDialogOpen(true);
    } else {
      console.error('💰 Product not found for price selection:', cartItem.productId);
      alert('No se pudo cargar la información del producto para mostrar los precios.');
    }
  };

  // Update item price in cart
  const updateItemPrice = (productId: string, priceLevel: number, newPrice: number, priceLevelLabel: string) => {
    setCart(cart.map(item =>
      item.productId === productId
        ? { ...item, price: newPrice, priceLevel, priceLevelLabel }
        : item
    ));
  };

  // Close price selection dialog
  const closePriceDialog = () => {
    setPriceDialogOpen(false);
    setSelectedItem(null);
    setPriceOptions([]);
    
    // Return focus for continuous scanning
    focusBarcodeForContinuousScanning();
  };

  // Calculate current discount amount dynamically
  const getCurrentDiscountAmount = (): number => {
    if (!discountApplied) return 0;
    
    if (discountApplied.isManual && discountApplied.fixedAmount) {
      // Manual discount - limit to current subtotal, rounded down
      return Math.floor(Math.min(discountApplied.fixedAmount, subtotalBeforeDiscount));
    } else if (discountApplied.type === DiscountType.PERCENTAGE && discountApplied.percentage) {
      // Percentage discount, rounded down
      return Math.floor(subtotalBeforeDiscount * (discountApplied.percentage / 100));
    } else if (discountApplied.type === DiscountType.FIXED_AMOUNT && discountApplied.fixedAmount) {
      // Fixed amount discount, rounded down
      return Math.floor(Math.min(discountApplied.fixedAmount, subtotalBeforeDiscount));
    }
    
    return 0;
  };

  // Handle discount application
  const applyDiscount = (discountId?: string, manualAmount?: number) => {
    if (manualAmount && manualAmount > 0 && canApplyManualDiscount) {
      let finalDiscountAmount = manualAmount;
      
      // Apply 10% limit for managers and cashiers
      if (isManager || isCashier) {
        const maxDiscountForNonAdmin = Math.floor(subtotalBeforeDiscount * 0.10);
        finalDiscountAmount = Math.min(manualAmount, maxDiscountForNonAdmin);

        if (finalDiscountAmount !== manualAmount) {
          const roleLabel = isCashier ? 'cajeros' : 'gerentes';
          alert(`Descuento limitado a 10% (L${maxDiscountForNonAdmin}) para ${roleLabel}`);
        }
      }
      // Admins have no limits - use the full amount
      
      // Apply manual discount
      setDiscountApplied({
        type: DiscountType.FIXED_AMOUNT,
        name: `Descuento Manual L${finalDiscountAmount.toFixed(2)}`,
        fixedAmount: finalDiscountAmount,
        isManual: true
      });
      setSelectedDiscountId('');
    } else {
      // Remove discount
      setDiscountApplied(null);
      setSelectedDiscountId('');
      setManualDiscountAmount(0);
    }
  };

  const removeDiscount = () => {
    setDiscountApplied(null);
    setSelectedDiscountId('');
    setManualDiscountAmount(0);
  };

  // Handle custom product (code "2")
  const handleCustomProduct = () => {
    setOpenCustomProductDialog(true);
    setCustomProductPrice(0);
  };

  const addCustomProduct = () => {
    if (customProductPrice <= 0) {
      alert('Por favor ingrese un precio válido mayor a 0');
      return;
    }

    // Generate automatic product name with price
    const productName = `Producto Varios - ${formatCurrency(Math.floor(customProductPrice))}`;

    const customProduct: CartItem = {
      productId: `custom-${Date.now()}`,
      name: productName,
      sku: 'VARIOS',
      barcode: '2',
      price: Math.floor(customProductPrice), // Round down to eliminate cents (tax inclusive)
      quantity: quickQuantity,
      taxRate: 15, // Tax is already included in the price
      stock: 999999, // Unlimited stock for custom products
    };

    setCart(prev => [customProduct, ...prev]);
    setLastAddedProductId(customProduct.productId);
    
    // Clear the highlight after 3 seconds
    setTimeout(() => {
      setLastAddedProductId(null);
    }, 3000);

    // Close dialog and reset values
    setOpenCustomProductDialog(false);
    setCustomProductPrice(0);
    setBarcodeInput('');
    
    // Return focus for continuous scanning
    focusBarcodeForContinuousScanning();
  };

  const closeCustomProductDialog = () => {
    setOpenCustomProductDialog(false);
    setCustomProductPrice(0);
    
    // Return focus for continuous scanning
    focusBarcodeForContinuousScanning();
  };

  // Buscar producto por código de barras
  const searchByBarcode = async (barcode: string) => {
    console.log('🔍 Buscando código de barras:', barcode);
    
    // Prevent multiple simultaneous requests
    if (isProcessingBarcode) {
      console.log('⏳ Already processing barcode, ignoring request');
      return false;
    }
    
    // Get the current token from localStorage since it's more reliable
    const currentToken = localStorage.getItem('accessToken');
    console.log('📝 AccessToken available:', !!currentToken);
    console.log('📝 AccessToken first chars:', currentToken?.substring(0, 20));
    
    // Check for special product code "2" - custom product
    if (barcode === '2') {
      setBarcodeInput('');
      setBarcodeError('');
      setLastScannedBarcode('');
      handleCustomProduct();
      return true;
    }
    
    setIsProcessingBarcode(true);
    setBarcodeError(''); // Clear any previous errors
    setLastScannedBarcode(barcode);
    
    try {
      // Primero intentar con el endpoint específico de barcode
      console.log('🚀 Calling barcode API for:', barcode);
      console.log('🔑 Using token:', currentToken?.substring(0, 20) + '...');
      
      const baseURL = getApiUrl();
      const url = `${baseURL}/products/barcode/${barcode}`;
      console.log('📡 Request URL:', url);
      
      const barcodeResponse = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('📨 Response status:', barcodeResponse.status);
      console.log('📨 Response statusText:', barcodeResponse.statusText);
      console.log('📨 Response headers:', Object.fromEntries(barcodeResponse.headers.entries()));
      
      if (barcodeResponse.ok) {
        const contentType = barcodeResponse.headers.get('content-type');
        console.log('📨 Content-Type:', contentType);
        
        if (!contentType || !contentType.includes('application/json')) {
          console.error('❌ Barcode API returned non-JSON response');
          const responseText = await barcodeResponse.text();
          console.error('❌ Response body:', responseText);
          // Fall through to try the general search
        } else {
          const data = await barcodeResponse.json();
          const foundProduct = data.data;
          
          if (foundProduct) {
            const productWithStock = {
              ...foundProduct,
              stock: foundProduct.inventories?.reduce((total: number, inv: any) => total + inv.quantity, 0) || 0,
              taxRate: typeof foundProduct.taxRate === 'number' && !isNaN(foundProduct.taxRate) ? foundProduct.taxRate : 15
            };
            addToCart(productWithStock, quickQuantity, true);
            // Mostrar feedback de éxito
            setLastScannedBarcode(barcode);
            setBarcodeError(''); // Clear any previous error
            // Limpiar inmediatamente para siguiente escaneo
            setBarcodeInput('');
            setIsProcessingBarcode(false);
            
            console.log('✅ Product added, barcode cleared');
            
            // Simple focus return after barcode scan
            setTimeout(() => {
              focusBarcodeForContinuousScanning(true);
            }, 200);
            
            // Limpiar feedback después de 3 segundos
            setTimeout(() => {
              setLastScannedBarcode('');
            }, 3000);
            return true;
          }
        }
      } else {
        console.error('❌ Barcode API error:', barcodeResponse.status, barcodeResponse.statusText);
        const errorText = await barcodeResponse.text();
        console.error('❌ Error response body:', errorText);
      }
      
      // Si no se encuentra por barcode, intentar buscar por código  
      const codeResponse = await fetch(`${baseURL}/products?search=${barcode}&limit=10`, {
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (codeResponse.ok) {
        const contentType = codeResponse.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.error('❌ API returned non-JSON response');
          return false;
        }
        
        const data = await codeResponse.json();
        // Buscar coincidencia exacta por código (case-insensitive)
        const foundProduct = data.products?.find((p: any) => p.code?.toLowerCase() === barcode.toLowerCase());
        
        if (foundProduct) {
          const productWithStock = {
            ...foundProduct,
            stock: foundProduct.inventories?.reduce((total: number, inv: any) => total + inv.quantity, 0) || 0,
            taxRate: typeof foundProduct.taxRate === 'number' && !isNaN(foundProduct.taxRate) ? foundProduct.taxRate : 15
          };
          addToCart(productWithStock, quickQuantity, true);
          // Mostrar feedback de éxito
          setLastScannedBarcode(barcode);
          setBarcodeError(''); // Clear any previous error
          // Limpiar inmediatamente para siguiente escaneo
          setBarcodeInput('');
          setIsProcessingBarcode(false);
          
          console.log('✅ Product added, barcode cleared');
          
          // Simple focus return after barcode scan
          setTimeout(() => {
            focusBarcodeForContinuousScanning(true);
          }, 200);
          
          // Limpiar feedback después de 3 segundos
          setTimeout(() => {
            setLastScannedBarcode('');
          }, 3000);
          return true;
        }
      } else {
        console.error('❌ API error:', codeResponse.status, codeResponse.statusText);
      }
    } catch (error) {
      console.error('Error buscando producto por código de barras:', error);
    } finally {
      setIsProcessingBarcode(false);
    }
    
    // If we get here, product was not found
    console.log('❌ Producto no encontrado:', barcode);
    setBarcodeError('Código no encontrado');
    setBarcodeInput(''); // Clear the input
    setIsProcessingBarcode(false);
    
    // Return focus for continuous scanning even when not found
    setTimeout(() => {
      focusBarcodeForContinuousScanning(true);
    }, 200);
    
    // Clear error message after 3 seconds
    setTimeout(() => {
      setBarcodeError('');
    }, 3000);
    
    return false;
  };
  
  // Manejar código de barras - buscar automáticamente cuando se ingresa un código completo
  useEffect(() => {
    const trimmedInput = barcodeInput.trim();
    
    console.log('🔍 Barcode input useEffect triggered');
    console.log('📝 Current barcodeInput:', barcodeInput);
    console.log('📝 Trimmed input:', trimmedInput);
    console.log('📝 Processing:', isProcessingBarcode);
    console.log('📝 Error:', barcodeError);
    
    // Clear error when user starts typing again
    if (trimmedInput && barcodeError) {
      setBarcodeError('');
    }
    
    // Verificar si es un código válido (solo números y longitud adecuada) o código especial "2"
    const isValidBarcode = (trimmedInput === '2') || (/^\d+$/.test(trimmedInput) && trimmedInput.length >= 12 && trimmedInput.length <= 15);
    
    console.log('📝 Barcode validation result:', {
      input: trimmedInput,
      isValid: isValidBarcode,
      length: trimmedInput.length,
      isNumeric: /^\d+$/.test(trimmedInput),
      isProcessing: isProcessingBarcode,
      hasError: !!barcodeError
    });
    
    if (isValidBarcode && !isProcessingBarcode && !barcodeError) {
      console.log('✅ Valid barcode detected, will search in 300ms...');
      // Pequeño delay para evitar procesamiento mientras se sigue escribiendo
      const timer = setTimeout(() => {
        console.log('🚀 Calling searchByBarcode now!');
        searchByBarcode(trimmedInput);
      }, 300);
      
      return () => clearTimeout(timer);
    } else {
      console.log('❌ Not searching because:', {
        isValid: isValidBarcode,
        isProcessing: isProcessingBarcode,
        hasError: !!barcodeError
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barcodeInput, isProcessingBarcode, barcodeError]);
  
  // Manejar Enter en código de barras (como respaldo)
  const handleBarcodeKeyPress = (e: React.KeyboardEvent) => {
    const trimmedInput = barcodeInput.trim();
    // Allow short keyphrases (like "cg", "220", "jumbona") or standard barcodes
    if (e.key === 'Enter' && trimmedInput.length >= 1) {
      searchByBarcode(trimmedInput);
    }
  };

  // Manejar paste en código de barras para procesamiento inmediato
  const handleBarcodePaste = (e: React.ClipboardEvent) => {
    setTimeout(() => {
      const pastedValue = e.clipboardData.getData('text').trim();
      const isValidBarcode = /^\d+$/.test(pastedValue) && pastedValue.length >= 8 && pastedValue.length <= 15;
      
      console.log('📋 Pasted value:', pastedValue, 'Valid:', isValidBarcode);
      
      if (isValidBarcode && !isProcessingBarcode) {
        searchByBarcode(pastedValue);
      }
    }, 50); // Delay mínimo para que el valor se actualice en el input
  };

  // Manejar búsqueda con Enter
  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && filteredProducts.length > 0) {
      addToCart(filteredProducts[0], quickQuantity);
    }
  };

  // Cart is already sorted with newest items at top, no need for additional sorting

  // Cálculos del carrito - Los precios YA incluyen impuestos (rounded down)
  const subtotalBeforeDiscount = Math.floor(cart.reduce((sum, item) => sum + (item.price * item.quantity), 0));
  
  // Calculate discount amount
  const discountAmount = getCurrentDiscountAmount();
  const subtotalAfterDiscount = Math.max(0, subtotalBeforeDiscount - discountAmount);
  
  const totalTax = Math.floor(cart.reduce((sum, item) => {
    const itemTotal = Math.floor(item.price * item.quantity);
    const taxRate = typeof item.taxRate === 'number' && !isNaN(item.taxRate) ? item.taxRate : 15; // Default to 15% if invalid
    // Calculate embedded tax: if price includes 15% tax, then tax = price - (price / 1.15)
    const taxAmount = itemTotal - (itemTotal / (1 + taxRate / 100));
    return sum + taxAmount;
  }, 0));
  
  // Apply discount proportionally to tax calculation (rounded down)
  const adjustedTotalTax = Math.floor(discountAmount > 0 ? (totalTax * (subtotalAfterDiscount / subtotalBeforeDiscount)) : totalTax);
  const subtotal = Math.floor(subtotalAfterDiscount - adjustedTotalTax);
  const total = subtotalAfterDiscount;
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Función para imprimir recibo térmico
  const printThermalReceipt = (saleData: any, cartItems: CartItem[], subtotalAmount: number, taxAmount: number, totalAmount: number) => {
    // Fix calculation for receipt: prices already include ISV
    const receiptSubtotal = totalAmount; // The total is actually the correct subtotal (prices with tax included)
    const receiptImporteGravado = receiptSubtotal / 1.15; // Base amount without 15% ISV
    const receiptISV = receiptSubtotal - receiptImporteGravado; // ISV amount
    
    // Format currency with 2 decimals for receipt
    const formatCurrencyPrecise = (amount: number) => {
      return `L ${amount.toFixed(2)}`;
    };
    const customerLine = saleData.customerName ? `<div style="margin: 5px 0;"><b>Cliente:</b> ${saleData.customerName}</div>` : '';
    const paymentMethodText = saleData.paymentMethod === 'CASH' ? 'Efectivo' : 
                                    saleData.paymentMethod === 'CARD' ? 'Tarjeta' : 
                                    saleData.paymentMethod === 'TRANSFER' ? 'Transferencia' : 
                                    saleData.paymentMethod === 'CHECK' ? 'Cheque' : 
                                    saleData.paymentMethod;
    
    // Calculate discount per item if there's a percentage discount
    const discountPercentage = discountApplied && discountApplied.type === DiscountType.PERCENTAGE ? (discountApplied.percentage || 0) : 0;
    
    const productLines = cartItems.map(item => {
      const originalPrice = item.price;
      const hasDiscount = discountPercentage > 0;
      const discountedPrice = hasDiscount ? originalPrice * (1 - discountPercentage / 100) : originalPrice;
      const itemTotal = discountedPrice * item.quantity;
      
      if (hasDiscount) {
        return `
          <div style="margin: 8px 0; border-bottom: 1px dotted #ccc; padding-bottom: 5px;">
            <div style="font-weight: bold;">${item.name}</div>
            <div style="font-size: 11px; color: #666; text-decoration: line-through;">Precio Normal: ${formatCurrency(originalPrice)}</div>
            <div style="font-size: 12px; color: #000; font-weight: bold;">Precio c/Desc: ${formatCurrency(discountedPrice)}</div>
            <div style="text-align: right;">${item.quantity} x ${formatCurrency(discountedPrice)} = <b>${formatCurrency(itemTotal)}</b></div>
          </div>`;
      } else {
        return `
          <div style="margin: 8px 0; border-bottom: 1px dotted #ccc; padding-bottom: 5px;">
            <div style="font-weight: bold;">${item.name}</div>
            <div style="text-align: right;">${item.quantity} x ${formatCurrency(originalPrice)} = <b>${formatCurrency(item.price * item.quantity)}</b></div>
          </div>`;
      }
    }).join('');
    
    const discountLine = discountAmount > 0 ? `
      <div style="margin: 5px 0; color: #d32f2f; font-weight: bold;">
        Descuento: -${formatCurrency(discountAmount)}
        ${discountApplied ? `<div style="font-size: 11px;">(${discountApplied.name})</div>` : ''}
      </div>` : '';
    
    // Crear elemento para impresión con HTML
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Recibo de Venta</title>
            <style>
              @media print {
                @page { margin: 0; }
                body { margin: 0; padding: 0; }
              }
              body { 
                font-family: Arial, sans-serif; 
                font-size: 13px; 
                font-weight: 600;
                width: 58mm; 
                margin: 0; 
                padding: 0 2mm;
                line-height: 1.2;
                color: #000 !important;
              }
              * {
                color: #000 !important;
              }
              .header {
                text-align: center;
                font-weight: bold;
                font-size: 16px;
                margin: 5px 0;
                color: #000 !important;
              }
              .divider {
                border-bottom: 2px solid #000;
                margin: 8px 0;
              }
              .date {
                text-align: center;
                margin: 5px 0;
                color: #000 !important;
              }
              .total-section {
                padding: 5px;
                margin: 10px 0;
                color: #000 !important;
              }
              .total-line {
                font-size: 18px;
                font-weight: bold;
                text-align: center;
                margin: 10px 0;
                padding: 8px;
                border: 2px solid #000;
                color: #000 !important;
              }
              .footer {
                text-align: center;
                margin-top: 15px;
                color: #000 !important;
              }
            </style>
          </head>
          <body>
            <div class="header">RECIBO DE VENTA</div>
            <div class="divider"></div>
            <div class="date">${new Date().toLocaleString('es-HN')}</div>
            ${customerLine}
            <div class="divider"></div>
            <div style="font-weight: bold; margin: 10px 0;">PRODUCTOS:</div>
            ${productLines}
            <div class="divider"></div>
            <div class="total-section">
              ${discountLine}
              <div style="display: flex; justify-content: space-between; margin: 5px 0;">
                <span>Importe Gravado:</span>
                <span><b>${formatCurrencyPrecise(receiptImporteGravado)}</b></span>
              </div>
              <div style="display: flex; justify-content: space-between; margin: 5px 0;">
                <span>ISV (15%):</span>
                <span><b>${formatCurrencyPrecise(receiptISV)}</b></span>
              </div>
            </div>
            <div class="total-line">
              TOTAL: ${formatCurrencyPrecise(receiptSubtotal)}
            </div>
            <div style="margin: 10px 0; text-align: center;">
              <b>Método de Pago:</b> ${paymentMethodText}
            </div>
            <div class="divider"></div>
            <div class="footer">
              ¡Gracias por su compra!
            </div>
            <script>
              window.onload = function() {
                window.print();
                setTimeout(() => window.close(), 1000);
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
    
    // Para impresoras térmicas ESC/POS, también podemos usar comandos directos
    if ('serial' in navigator) {
      // TODO: Implementar comunicación directa con impresora térmica vía Web Serial API
      console.log('Enviando a impresora térmica ESC/POS...');
    }
  };

  const handleCheckout = async () => {
    const saleData = {
      customerName: customerName || undefined,
      paymentMethod,
      employeeCode: employeeCode || undefined,
      items: cart.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        unitPrice: item.price,
        // Include additional fields for custom products
        ...(item.productId.startsWith('custom-') && {
          isCustomProduct: true
        })
      })),
      // Include discount information if applied
      ...(discountApplied && {
        discount: {
          type: discountApplied.type,
          name: discountApplied.name,
          percentage: discountApplied.percentage,
          fixedAmount: discountApplied.fixedAmount,
          isManual: discountApplied.isManual,
          amount: getCurrentDiscountAmount()
        }
      })
    };
    
    console.log('Procesando venta:', saleData);
    
    try {
      const result = await createSale(saleData).unwrap();
      
      // Generar recibo para impresión
      printThermalReceipt(saleData, cart, subtotal, totalTax, total);
      
      // Limpiar y preparar para siguiente venta
      clearCart();
      setEmployeeCode('');
      setSelectedEmployee(null);
      setCustomerName('');
      setOpenCheckout(false);
      
      // Return focus for next sale
      focusBarcodeForContinuousScanning();
      
      // Mostrar mensaje breve de éxito
      console.log('✅ Venta procesada exitosamente!', result);
      
      // Mostrar información de comisión si aplica
      if (result.data.commission) {
        console.log(`💰 Comisión de ${result.data.commission.percentage}% (L${result.data.commission.amount.toFixed(2)}) asignada a ${result.data.commission.user.name}`);
      }
      
    } catch (error) {
      console.error('Error procesando venta:', error);
      
      // More detailed error handling
      let errorMessage = 'Error al procesar la venta. Inténtalo de nuevo.';
      if (error && typeof error === 'object' && 'data' in error) {
        const apiError = error as any;
        if (apiError.data?.message) {
          errorMessage = `Error: ${apiError.data.message}`;
        } else if (apiError.data?.error) {
          errorMessage = `Error: ${apiError.data.error}`;
        }
      }
      
      alert(errorMessage);
    }
  };

  const handleSaveDraft = async () => {
    if (cart.length === 0) {
      return;
    }

    const draftData = {
      draftName: draftName || `Borrador ${new Date().toLocaleString('es-ES')}`,
      customerName: customerName || undefined,
      items: cart.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        unitPrice: item.price,
      })),
      // Include discount information if applied
      ...(discountApplied && {
        discount: {
          type: discountApplied.type,
          name: discountApplied.name,
          value: discountApplied.percentage || discountApplied.fixedAmount || 0,
          isManual: discountApplied.isManual || false,
        }
      })
    };

    try {
      await saveDraft(draftData).unwrap();
      
      // Clear the cart and reset form
      clearCart();
      setCustomerName('');
      setEmployeeCode('');
      setDiscountApplied(null);
      setSelectedDiscountId('');
      setManualDiscountAmount(0);
      setDraftName('');
      setOpenSaveDraftDialog(false);
      
      // Return focus for continuous scanning
      focusBarcodeForContinuousScanning();
      
    } catch (error) {
      console.error('Error saving draft:', error);
      let errorMessage = 'Error al guardar el borrador. Inténtalo de nuevo.';
      if (error && typeof error === 'object' && 'data' in error) {
        const apiError = error as any;
        if (apiError.data?.message) {
          errorMessage = `Error: ${apiError.data.message}`;
        }
      }
      alert(errorMessage);
    }
  };

  const loadDraft = (draft: any) => {
    try {
      // Clear current cart
      clearCart();

      // Load customer name
      setCustomerName(draft.customerName || '');

      // Load items to cart
      const cartItems: CartItem[] = draft.saleItems.map((item: any) => ({
        productId: item.productId,
        name: item.product.name,
        sku: item.product.sku,
        barcode: item.product.barcode,
        price: item.unitPrice,
        quantity: item.quantity,
        taxRate: 15, // Default tax rate
        stock: 999 // We'll assume stock is available when loading draft
      }));

      setCart(cartItems);

      // Load discount if applied
      if (draft.discountAmount > 0) {
        setDiscountApplied({
          type: draft.discountType as DiscountType,
          name: draft.discountName || 'Descuento aplicado',
          percentage: draft.discountPercentage,
          fixedAmount: draft.discountFixedAmount,
          isManual: draft.discountIsManual
        });
      }

      // Close the drafts dialog
      setOpenDraftsDialog(false);
      
      // Return focus for continuous scanning
      focusBarcodeForContinuousScanning();
    } catch (error) {
      console.error('Error loading draft:', error);
      alert('Error al cargar el borrador. Inténtalo de nuevo.');
    }
  };

  const handleDeleteDraft = async (draftId: string) => {
    try {
      await deleteDraft(draftId).unwrap();
      refetchDrafts(); // Refresh the drafts list
    } catch (error) {
      console.error('Error deleting draft:', error);
      alert('Error al eliminar el borrador. Inténtalo de nuevo.');
    }
  };

  // Enhanced focus management for continuous scanning
  useEffect(() => {
    // Focus barcode field when not in checkout and not processing
    const dialogsOpen = openCheckout || openSaveDraftDialog || openDraftsDialog || openCustomProductDialog || priceDialogOpen;
    
    if (!dialogsOpen && !isProcessingBarcode && barcodeRef.current) {
      const focusBarcode = () => {
        if (barcodeRef.current && document.activeElement !== barcodeRef.current) {
          barcodeRef.current.focus();
          console.log('🎯 Auto-focused barcode field (useEffect)');
        }
      };
      
      // Focus on initial load and state changes
      setTimeout(focusBarcode, 100);
    }
  }, [openCheckout, openSaveDraftDialog, openDraftsDialog, openCustomProductDialog, priceDialogOpen, isProcessingBarcode]);
  
  // Focus barcode field when closing dialogs
  useEffect(() => {
    if (!openCheckout && !openSaveDraftDialog && !openDraftsDialog && !openCustomProductDialog && !priceDialogOpen) {
      focusBarcodeForContinuousScanning();
    }
  }, [openCheckout, openSaveDraftDialog, openDraftsDialog, openCustomProductDialog, priceDialogOpen]);

  return (
    <Box>
      {/* Header - Optimized for space */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1, px: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<SaveIcon />}
          onClick={() => setOpenDraftsDialog(true)}
          sx={{ 
            borderColor: 'info.main',
            color: 'info.main',
            '&:hover': {
              backgroundColor: 'info.light',
              borderColor: 'info.dark'
            }
          }}
        >
          📋 Mis Borradores
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)', gap: 1 }}>
      {/* Panel de búsqueda y productos */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Last scanned barcode status at the very top */}
        <Box sx={{ mb: 1, textAlign: 'center' }}>
          {lastScannedBarcode && !barcodeError && (
            <Typography variant="body2" sx={{ 
              fontFamily: 'monospace',
              backgroundColor: 'success.light',
              color: 'success.dark',
              borderRadius: 1,
              p: 0.5,
              display: 'inline-block',
              fontWeight: 'bold'
            }}>
              ✅ Último agregado: {lastScannedBarcode}
            </Typography>
          )}
          {barcodeError && (
            <Typography variant="body2" sx={{ 
              fontFamily: 'monospace',
              backgroundColor: 'error.light',
              color: 'error.dark',
              borderRadius: 1,
              p: 0.5,
              display: 'inline-block',
              fontWeight: 'bold'
            }}>
              ❌ {barcodeError}
            </Typography>
          )}
        </Box>


        {/* Scanner and Employee Section - Side by Side */}
        <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
          {/* Scanner Section */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mb: 0.5 }}>
              🔍 ESCÁNER
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                id="barcode-input-field"
                ref={barcodeRef}
                label={isProcessingBarcode ? "Procesando..." : "Código"}
                placeholder="Escanear código"
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                onKeyPress={handleBarcodeKeyPress}
                onPaste={handleBarcodePaste}
                onFocus={() => {
                  setBarcodeFieldFocused(true);
                  console.log('📍 Barcode field focused');
                }}
                onBlur={(e) => {
                  // Prevent blur if we're in the middle of barcode processing
                  if (isProcessingBarcode) {
                    console.log('📍 Preventing blur during barcode processing');
                    e.target.focus();
                    return;
                  }
                  
                  setBarcodeFieldFocused(false);
                  console.log('📍 Barcode field blurred');
                }}
                disabled={isProcessingBarcode}
                autoFocus
                size="small"
                sx={{ 
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isProcessingBarcode ? '#f5f5f5' : 'white',
                  },
                  '& .MuiInputBase-input': {
                    textAlign: 'center',
                    fontFamily: 'monospace',
                  }
                }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {isProcessingBarcode ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={20} />
                        <ScannerIcon sx={{ fontSize: 28 }} />
                      </Box>
                    ) : (
                      <ScannerIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                    )}
                  </InputAdornment>
                ),
              }}
              autoComplete="off"
            />
              <TextField
                label="Cant."
                type="number"
                value={quickQuantity}
                onChange={(e) => setQuickQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                size="small"
                sx={{ 
                  width: 70,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                  }
                }}
                inputProps={{ min: 1 }}
                autoComplete="off"
              />
            </Box>
          </Box>

          {/* Employee Section */}
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mb: 0.5 }}>
              💼 EMPLEADO
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                label="Código"
                placeholder="EMP001"
                value={employeeCode}
                onChange={(e) => setEmployeeCode(e.target.value.toUpperCase())}
                size="small"
                sx={{ 
                  minWidth: 120,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                  },
                  '& .MuiInputBase-input': {
                    textAlign: 'center',
                    fontFamily: 'monospace',
                  }
                }}
                autoComplete="off"
              />
            {selectedEmployee && (
              <Box sx={{ 
                backgroundColor: 'rgba(255,255,255,0.9)', 
                color: 'success.dark',
                borderRadius: 1, 
                p: 1,
                textAlign: 'center',
                minWidth: 150
              }}>
                <Typography variant="caption" fontWeight="bold">
                  ✅ {selectedEmployee.name}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                  {selectedEmployee.commissionRate}%
                </Typography>
              </Box>
            )}
            {employeeCode && !selectedEmployee && (
              <Box sx={{ 
                backgroundColor: 'rgba(255,255,255,0.7)', 
                color: 'error.dark',
                borderRadius: 1, 
                p: 1,
                textAlign: 'center',
                minWidth: 150
              }}>
                <Typography variant="caption">
                  ❌ No encontrado
                </Typography>
              </Box>
            )}
            </Box>
          </Box>
        </Box>

        {/* Búsqueda manual (secundaria) */}
        <Paper sx={{ p: 1, mb: 1 }}>
          <TextField
            ref={searchRef}
            fullWidth
            label="Búsqueda de productos"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            size="small"
            sx={{ mb: 0.5 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            Enter para agregar • Mín. 2 caracteres
          </Typography>
        </Paper>

        {/* Lista de productos */}
        <Paper sx={{ flex: 1, overflow: 'auto' }}>
          {productsError && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="error">Error al cargar productos</Typography>
            </Box>
          )}
          {(isLoadingProducts || isLoadingWildcard) && searchTerm.length >= 2 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography>Buscando productos{isWildcardPattern ? ' con patrón' : ''}...</Typography>
            </Box>
          )}
          {!(isLoadingProducts || isLoadingWildcard) && !productsError && !wildcardError && searchTerm.length >= 2 ? (
            <List>
              {filteredProducts.map((product) => (
                <ListItem
                  key={product.id}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    py: 0.5,
                    px: 1,
                  }}
                  onClick={() => addToCart(product, quickQuantity)}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.85rem' }}>
                          {product.name}
                        </Typography>
                        <Chip 
                          label={product.category} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ height: 16, fontSize: '0.65rem' }}
                        />
                        {product.stock <= 0 && (
                          <Chip 
                            label="Sin Stock"
                            size="small" 
                            color="warning" 
                            sx={{ height: 16, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          SKU: {product.sku}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          Stock: {product.stock}
                        </Typography>
                      </Box>
                    }
                  />
                  <Box sx={{ textAlign: 'right', minWidth: '120px' }}>
                    <Typography variant="body2" color="primary" fontWeight="bold" sx={{ mb: 0.3 }}>
                      {formatCurrency(product.salePrice)}
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        addToCart(product, quickQuantity);
                      }}
                      sx={{ fontSize: '0.7rem', py: 0.3, px: 1 }}
                    >
                      Agregar
                    </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : null}
        </Paper>
      </Box>

      {/* Panel del carrito */}
      <Paper 
        elevation={4}
        sx={{ 
          flex: '0 0 auto',
          height: '450px',
          p: 1.5, 
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: 'background.paper',
          border: '2px solid',
          borderColor: 'primary.main',
          borderRadius: 2
        }}
      >
        {/* Cart actions toolbar */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 2,
          p: 1,
          backgroundColor: 'grey.100',
          borderRadius: 1
        }}>
          <Chip 
            icon={<CartIcon />}
            label={`${totalItems} productos`}
            sx={{ 
              fontWeight: 'bold',
              fontSize: '0.9rem'
            }}
          />
          {cart.length > 0 && (
            <IconButton
              size="small"
              onClick={clearCart}
              title="Limpiar carrito"
              sx={{
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.light'
                }
              }}
            >
              <ClearAllIcon />
            </IconButton>
          )}
        </Box>

        {/* Customer Name and Payment Method in one row */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            label="👤 Cliente (Opcional)"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            size="small"
            sx={{ 
              flex: 1,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'grey.50',
                fontSize: '0.9rem'
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.85rem'
              }
            }}
            variant="outlined"
          />
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ fontSize: '0.85rem' }}>🏦 Pago</InputLabel>
            <Select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
              label="🏦 Pago"
              sx={{ 
                fontSize: '0.9rem',
                backgroundColor: 'grey.50'
              }}
            >
              <MenuItem value={PaymentMethod.CASH} sx={{ fontSize: '0.9rem' }}>💵 Efectivo</MenuItem>
              <MenuItem value={PaymentMethod.CARD} sx={{ fontSize: '0.9rem' }}>💳 Tarjeta</MenuItem>
              <MenuItem value={PaymentMethod.TRANSFER} sx={{ fontSize: '0.9rem' }}>🏦 Transfer</MenuItem>
              <MenuItem value={PaymentMethod.CHECK} sx={{ fontSize: '0.9rem' }}>📝 Cheque</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Cart Content - Horizontal Layout */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex',
          gap: 1,
          overflow: 'hidden',
          minHeight: 0
        }}>
          {/* Lista del carrito */}
          <Box sx={{ 
            flex: '0 1 60%', 
            overflow: 'auto', 
            border: '2px solid',
            borderColor: 'primary.main',
            borderRadius: 2,
            backgroundColor: 'white'
          }}>
          {cart.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 2,
              px: 2
            }}>
              <Typography variant="body2" color="text.secondary">
                Carrito vacío - Escanea códigos o busca productos
              </Typography>
            </Box>
          ) : (
            <Box>
              
              {/* Scrollable Items List */}
              <Box sx={{ 
                height: '100%', 
                overflowY: 'auto',
                p: 1,
                '&::-webkit-scrollbar': {
                  width: 6,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'grey.200',
                  borderRadius: 3,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'primary.main',
                  borderRadius: 3,
                },
              }}>
                {cart.map((item, index) => (
                <Box 
                  key={item.productId}
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    mx: 1,
                    mb: 1,
                    backgroundColor: item.productId === lastAddedProductId ? 'success.light' : 
                                   index % 2 === 0 ? 'grey.50' : 'white',
                    borderRadius: 1,
                    border: '2px solid',
                    borderColor: item.productId === lastAddedProductId ? 'success.main' : 'grey.200',
                    '&:hover': { 
                      backgroundColor: 'primary.light',
                      borderColor: 'primary.main',
                      boxShadow: 1
                    },
                    transition: 'all 0.3s ease',
                    ...(item.productId === lastAddedProductId && {
                      boxShadow: 2,
                      transform: 'scale(1.02)',
                    })
                  }}
                >
                  {/* Product Info - Takes most space */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="body2" 
                      fontWeight="bold" 
                      sx={{ 
                        mb: 0.5,
                        lineHeight: 1.2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {item.productId === lastAddedProductId && (
                        <span style={{ color: '#2e7d32', marginRight: 4 }}>🆕 </span>
                      )}
                      {item.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatCurrency(item.price)} c/u
                      </Typography>
                      {item.stock < 0 && (
                        <Chip 
                          label="⚠️" 
                          size="small" 
                          color="warning"
                          sx={{ height: 16, fontSize: '0.6rem', minWidth: 16 }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Quantity Controls - Compact with editable input */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    backgroundColor: 'white',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    overflow: 'hidden'
                  }}>
                    <IconButton
                      size="small"
                      onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                      sx={{ 
                        borderRadius: 0,
                        backgroundColor: 'error.light',
                        color: 'error.dark',
                        '&:hover': { backgroundColor: 'error.main', color: 'error.contrastText' }
                      }}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handlePriceSelection(item)}
                      sx={{ 
                        borderRadius: 0,
                        backgroundColor: 'primary.light',
                        color: 'primary.dark',
                        '&:hover': { backgroundColor: 'primary.main', color: 'primary.contrastText' },
                        ml: 0.5
                      }}
                      title={`Precio actual: ${item.priceLevelLabel || 'Regular'}`}
                    >
                      <PriceIcon fontSize="small" />
                    </IconButton>
                    <TextField
                      size="small"
                      type="number"
                      value={editingQuantity[item.productId] !== undefined ? editingQuantity[item.productId] : item.quantity}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        // Store the raw input value for editing
                        setEditingQuantity(prev => ({
                          ...prev,
                          [item.productId]: inputValue
                        }));
                      }}
                      onFocus={(e) => {
                        // Select all text when focused for easy editing
                        e.target.select();
                        // Initialize editing state with current quantity
                        setEditingQuantity(prev => ({
                          ...prev,
                          [item.productId]: item.quantity.toString()
                        }));
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          // Apply the quantity change
                          const inputValue = editingQuantity[item.productId];
                          const newQuantity = Math.max(1, parseInt(inputValue) || 1);
                          updateQuantity(item.productId, newQuantity);
                          // Clear editing state
                          setEditingQuantity(prev => {
                            const newState = { ...prev };
                            delete newState[item.productId];
                            return newState;
                          });
                          focusBarcodeForContinuousScanning();
                        }
                      }}
                      onBlur={() => {
                        // Apply the quantity change when losing focus
                        const inputValue = editingQuantity[item.productId];
                        if (inputValue !== undefined) {
                          const newQuantity = Math.max(1, parseInt(inputValue) || 1);
                          updateQuantity(item.productId, newQuantity);
                          // Clear editing state
                          setEditingQuantity(prev => {
                            const newState = { ...prev };
                            delete newState[item.productId];
                            return newState;
                          });
                        }
                        // Return focus to barcode after editing quantity
                        focusBarcodeForContinuousScanning();
                      }}
                      inputProps={{ 
                        min: 1,
                        style: { 
                          textAlign: 'center',
                          width: 45,
                          padding: '4px 0',
                          fontSize: '0.9rem',
                          fontWeight: 'bold'
                        }
                      }}
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'grey.100',
                          borderRadius: 0,
                          '& fieldset': { border: 'none' },
                          '&:hover fieldset': { border: 'none' },
                          '&.Mui-focused fieldset': { border: '2px solid', borderColor: 'primary.main' }
                        }
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                      sx={{ 
                        borderRadius: 0,
                        backgroundColor: 'success.light',
                        color: 'success.dark',
                        '&:hover': { backgroundColor: 'success.main', color: 'success.contrastText' }
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Total Price */}
                  <Box sx={{ textAlign: 'right', minWidth: 70 }}>
                    <Typography variant="body1" fontWeight="bold" color="primary.main" sx={{ fontSize: '0.95rem' }}>
                      {formatCurrency(item.price * item.quantity)}
                    </Typography>
                  </Box>

                  {/* Delete Button */}
                  <IconButton
                    size="small"
                    onClick={() => removeFromCart(item.productId)}
                    sx={{ 
                      backgroundColor: 'error.light',
                      color: 'error.dark',
                      '&:hover': { 
                        backgroundColor: 'error.main', 
                        color: 'error.contrastText',
                        transform: 'scale(1.1)'
                      }
                    }}
                    title="Eliminar"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                ))}
              </Box>
            </Box>
          )}
          </Box>
          
          {/* Right Side Panel - Discount & Totals */}
          {cart.length > 0 && (
            <Box sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              overflow: 'auto'
            }}>

              {/* Discount Section */}
              <Paper sx={{ p: 1, backgroundColor: 'warning.light', border: '1px solid', borderColor: 'warning.main' }}>
                <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, color: 'warning.dark', fontSize: '0.75rem' }}>
                  🎯 DESCUENTOS
                </Typography>
                
                {/* Predefined Discounts - DISABLED for security */}
                {/* Predefined discounts have been disabled due to security vulnerabilities */}

                {/* Manual Discount (Admin & Manager) */}
                {canApplyManualDiscount && (
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <TextField
                  size="small"
                  label={isAdmin ? "Descuento Manual (L)" : "Descuento Manual (L) - Máx 10%"}
                  type="number"
                  value={manualDiscountAmount}
                  onChange={(e) => setManualDiscountAmount(parseFloat(e.target.value) || 0)}
                  inputProps={{ 
                    min: 0, 
                    ...((isManager || isCashier) && { max: Math.floor(subtotalBeforeDiscount * 0.10) }), // Limit managers and cashiers
                    step: 0.01 
                  }}
                  sx={{ 
                    flexBasis: '80%', 
                    backgroundColor: 'white',
                    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
                    '& .MuiInputBase-input': { fontSize: '0.8rem' }
                  }}
                />
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => applyDiscount(undefined, manualDiscountAmount)}
                  disabled={!manualDiscountAmount || manualDiscountAmount <= 0}
                  sx={{ 
                    fontSize: '0.75rem', 
                    px: 1, 
                    backgroundColor: 'info.main',
                    '&:hover': {
                      backgroundColor: 'info.dark'
                    },
                    flexBasis: '20%'
                  }}
                >
                  Aplicar
                </Button>
              </Box>
            )}

            {/* Applied Discount Display */}
            {discountApplied && (
              <Box sx={{ 
                mt: 0.5, 
                p: 0.5, 
                backgroundColor: 'info.light', 
                borderRadius: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                minHeight: 'auto',
                border: '1px solid',
                borderColor: 'info.main'
              }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="caption" fontWeight="bold" color="info.dark" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                    💰 {discountApplied.name}
                  </Typography>
                  <Typography variant="caption" display="block" color="info.dark" sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}>
                    Descuento: -{formatCurrency(getCurrentDiscountAmount())}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={removeDiscount} sx={{ color: 'error.main', padding: '2px', minWidth: 'auto' }}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            )}
          </Paper>
              
              {/* Totales y checkout */}
              <Box sx={{ 
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                overflow: 'hidden'
              }}>
                <Box sx={{ 
                  p: 1, 
                  backgroundColor: 'success.light',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'success.main'
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.75rem' }}>Subtotal:</Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', whiteSpace: 'nowrap', minWidth: 'fit-content' }}>{formatCurrency(subtotal)}</Typography>
                  </Box>
                  {discountAmount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, gap: 0.5 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: 'error.main' }}>Descuento:</Typography>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: 'error.main', whiteSpace: 'nowrap', minWidth: 'fit-content' }}>-{formatCurrency(discountAmount)}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.75rem' }}>ISV (15%):</Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', whiteSpace: 'nowrap', minWidth: 'fit-content' }}>{formatCurrency(adjustedTotalTax)}</Typography>
                  </Box>
                  
                  <Divider sx={{ my: 1, borderColor: 'success.dark' }} />
                  
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1,
                    backgroundColor: 'success.main',
                    borderRadius: 1,
                    color: 'success.contrastText',
                    gap: 1
                  }}>
                    <Typography fontWeight="bold" sx={{ fontSize: '1rem' }}>TOTAL:</Typography>
                    <Typography fontWeight="bold" sx={{ fontSize: '1.1rem', whiteSpace: 'nowrap' }}>
                      {formatCurrency(total)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Dialog de confirmación */}
      <Dialog open={openCheckout} onClose={() => setOpenCheckout(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon />
            Confirmar Venta
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Revisa los detalles de la venta antes de procesarla
          </Alert>
          
          <Typography variant="h6" gutterBottom>
            Resumen de la Venta
          </Typography>
          
          {customerName && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Cliente:</strong> {customerName}
            </Typography>
          )}
          
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Producto</TableCell>
                  <TableCell align="center">Cant.</TableCell>
                  <TableCell align="right">Precio</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cart.map((item) => (
                  <TableRow key={item.productId}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell align="center">{item.quantity}</TableCell>
                    <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.price * item.quantity)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>Subtotal (sin ISV):</Typography>
            <Typography>{formatCurrency(subtotal)}</Typography>
          </Box>
          {discountAmount > 0 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="error.main">Descuento aplicado:</Typography>
                <Typography color="error.main">-{formatCurrency(discountAmount)}</Typography>
              </Box>
              {discountApplied && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">({discountApplied.name})</Typography>
                  <Typography variant="body2" color="text.secondary"></Typography>
                </Box>
              )}
            </>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>ISV incluido (15%):</Typography>
            <Typography>{formatCurrency(adjustedTotalTax)}</Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight="bold">Total:</Typography>
            <Typography variant="h6" fontWeight="bold" color="primary">
              {formatCurrency(total)}
            </Typography>
          </Box>
          
          <Typography variant="body2" sx={{ mt: 2 }}>
            <strong>Método de pago:</strong> {
              paymentMethod === PaymentMethod.CASH ? '💵 Efectivo' :
              paymentMethod === PaymentMethod.CARD ? '💳 Tarjeta' : 
              paymentMethod === PaymentMethod.TRANSFER ? '🏦 Transferencia' :
              paymentMethod === PaymentMethod.CHECK ? '📝 Cheque' : 
              paymentMethod
            }
          </Typography>
          
          {/* Información de comisión */}
          {selectedEmployee && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  💰 Comisión asignada a: {selectedEmployee.name}
                </Typography>
                <Typography variant="caption">
                  {selectedEmployee.commissionRate || 0}% de L{total.toFixed(2)} = L{(total * ((selectedEmployee.commissionRate || 0) / 100)).toFixed(2)}
                </Typography>
              </Box>
            </Alert>
          )}
          
          {employeeCode && !selectedEmployee && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                ⚠️ El código de empleado "{employeeCode}" no es válido. La comisión se asignará al cajero.
              </Typography>
            </Alert>
          )}
          
          {!employeeCode && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                ℹ️ No se especificó código de empleado. La comisión se asignará al cajero.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenCheckout(false)} variant="outlined">
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            onClick={handleCheckout}
            startIcon={isCreatingSale ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
            disabled={isCreatingSale}
          >
            {isCreatingSale ? 'Procesando...' : 'Confirmar y Imprimir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Product Dialog */}
      <Dialog open={openCustomProductDialog} onClose={closeCustomProductDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon />
            Producto Varios
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Ingrese el precio del producto (impuesto incluido)
            </Typography>
            <TextField
              fullWidth
              label="💰 Precio (Lempiras)"
              type="number"
              value={customProductPrice || ''}
              onChange={(e) => setCustomProductPrice(parseFloat(e.target.value) || 0)}
              inputProps={{ min: 1, step: 1 }}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  fontSize: '1.2rem',
                  height: '56px'
                }
              }}
              autoFocus
              placeholder="0"
            />
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: 1, 
              mb: 2,
              p: 1,
              backgroundColor: 'grey.100',
              borderRadius: 1
            }}>
              <Typography variant="body2" color="text.secondary">
                Cantidad:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {quickQuantity}
              </Typography>
            </Box>
            {customProductPrice > 0 && (
              <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'bold' }}>
                Total: {formatCurrency(Math.floor(customProductPrice) * quickQuantity)}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCustomProductDialog}>
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            onClick={addCustomProduct}
            disabled={customProductPrice <= 0}
            sx={{ fontSize: '1rem', px: 3 }}
          >
            ✅ Agregar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Draft Dialog */}
      <Dialog open={openSaveDraftDialog} onClose={() => setOpenSaveDraftDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SaveIcon />
            Guardar Borrador
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Nombre del borrador (opcional)"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder={`Borrador ${new Date().toLocaleString('es-ES')}`}
              sx={{ mb: 2 }}
            />
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {cart.length === 1 ? 
                `Se guardará 1 producto en el borrador.` :
                `Se guardarán ${cart.length} productos en el borrador.`
              }
            </Typography>
            
            {customerName && (
              <Typography variant="body2" color="text.secondary">
                <strong>Cliente:</strong> {customerName}
              </Typography>
            )}
            
            {discountApplied && (
              <Typography variant="body2" color="text.secondary">
                <strong>Descuento aplicado:</strong> {discountApplied.name}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSaveDraftDialog(false)}>
            Cancelar
          </Button>
          <Button 
            variant="contained"
            onClick={handleSaveDraft}
            disabled={isSavingDraft}
            startIcon={<SaveIcon />}
          >
            {isSavingDraft ? 'Guardando...' : 'Guardar Borrador'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drafts Dialog */}
      <Dialog open={openDraftsDialog} onClose={() => setOpenDraftsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ListIcon />
            Mis Borradores
          </Box>
        </DialogTitle>
        <DialogContent>
          {draftsData?.success && draftsData.data?.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Nombre</strong></TableCell>
                    <TableCell><strong>Cliente</strong></TableCell>
                    <TableCell align="center"><strong>Productos</strong></TableCell>
                    <TableCell align="right"><strong>Total</strong></TableCell>
                    <TableCell align="center"><strong>Fecha</strong></TableCell>
                    <TableCell align="center"><strong>Acciones</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {draftsData.data.map((draft) => (
                    <TableRow key={draft.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {draft.draftName || `Borrador ${new Date(draft.createdAt).toLocaleDateString('es-ES')}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {draft.customerName || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip 
                          title={
                            <Box sx={{ p: 1 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                Productos en el borrador:
                              </Typography>
                              {draft.saleItems.map((item, index) => (
                                <Box key={index} sx={{ mb: 0.5 }}>
                                  <Typography variant="body2">
                                    • {item.product.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    Cantidad: {item.quantity} | Precio: {formatCurrency(item.unitPrice)} | Total: {formatCurrency(item.total)}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          <Chip 
                            label={`${draft.saleItems.length} ${draft.saleItems.length === 1 ? 'producto' : 'productos'}`} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            sx={{ cursor: 'help' }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium" color="primary.main">
                          {formatCurrency(draft.total)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption" color="text.secondary">
                          {new Date(draft.updatedAt).toLocaleDateString('es-ES', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<PlayArrowIcon />}
                            onClick={() => loadDraft(draft)}
                            sx={{ fontSize: '0.75rem', px: 1 }}
                          >
                            Cargar
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDeleteDraft(draft.id)}
                            disabled={isDeletingDraft}
                            sx={{ fontSize: '0.75rem', px: 1 }}
                          >
                            Eliminar
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                📝 No hay borradores guardados
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Los borradores que guardes aparecerán aquí para poder reanudarlos más tarde.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDraftsDialog(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Floating Action Buttons */}
      {cart.length > 0 && (
        <>
          {/* Save Draft Button */}
          <Fab
            color="default"
            aria-label="guardar borrador"
            onClick={() => setOpenSaveDraftDialog(true)}
            disabled={isSavingDraft}
            sx={{
              position: 'fixed',
              bottom: 20,
              right: 230,
              backgroundColor: 'white',
              color: 'success.main',
              border: '2px solid',
              borderColor: 'success.main',
              '&:hover': {
                backgroundColor: 'success.light',
              },
              '&.Mui-disabled': {
                backgroundColor: 'grey.200',
              }
            }}
          >
            <SaveIcon />
          </Fab>

          {/* Process Sale Button */}
          <Fab
            color="primary"
            aria-label="procesar venta"
            onClick={() => setOpenCheckout(true)}
            disabled={isCreatingSale}
            sx={{
              position: 'fixed',
              bottom: 20,
              right: 160,
              width: 56,
              height: 56,
              '&:hover': {
                transform: 'scale(1.05)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <CheckIcon />
          </Fab>
        </>
      )}

      {/* Price Selection Dialog */}
      <Dialog open={priceDialogOpen} onClose={closePriceDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Seleccionar Precio
          <IconButton
            aria-label="close"
            onClick={closePriceDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedItem && (
            <>
              <Typography variant="h6" gutterBottom>
                {selectedItem.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                SKU: {selectedItem.sku}
              </Typography>
              
              <List>
                {priceOptions.map((option) => (
                  <ListItem
                    key={option.level}
                    component="div"
                    onClick={() => {
                      updateItemPrice(selectedItem.productId, option.level, option.price, option.label);
                      closePriceDialog();
                      // Focus is handled by closePriceDialog
                    }}
                    sx={{ 
                      border: '1px solid',
                      borderColor: selectedItem.priceLevel === option.level ? 'primary.main' : 'grey.300',
                      mb: 1,
                      borderRadius: 1,
                      backgroundColor: selectedItem.priceLevel === option.level ? 'primary.light' : 'transparent',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: selectedItem.priceLevel === option.level ? 'primary.light' : 'grey.50'
                      }
                    }}
                  >
                    <ListItemIcon>
                      <PriceIcon color={selectedItem.priceLevel === option.level ? 'primary' : 'action'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={option.label}
                      secondary={formatCurrency(option.price)}
                      primaryTypographyProps={{
                        fontWeight: selectedItem.priceLevel === option.level ? 'bold' : 'normal'
                      }}
                    />
                    {selectedItem.priceLevel === option.level && (
                      <CheckIcon color="primary" />
                    )}
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
      </Dialog>

      </Box>
    </Box>
  );
};

export default SalesPage;